/**
 * 가상 체험 진입 스플래시
 * URL: /demo/parent · /demo/owner
 * 1등급 로고 + 안내 → 1.4초 후 자동 데모 로그인 → 해당 화면 이동
 */
import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { auth } from '../../firebase/config';
import { APP_CONFIG } from '../../config/appConfig';
import { useAcademy } from '../../context/AcademyContext';

const ROLES = {
  parent: { mode: 'personal', label: '학부모', to: '/app/home', key: 'parentUid' },
  owner: { mode: 'academy', label: '학원', to: '/app/biz', key: 'ownerUid' },
};

const DEMO_SESSION_KEY = 'topgrade.demoSession';

const DemoIntroPage = () => {
  const { role } = useParams();
  const navigate = useNavigate();
  const { setMode } = useAcademy();
  const cfg = ROLES[role] || ROLES.parent;

  useEffect(() => {
    let alive = true;
    (async () => {
      try { await signOut(auth); } catch {}

      // 로고 최소 표시 시간 — 함수 호출과 병렬 진행
      const introDelay = new Promise((r) => setTimeout(r, 600));

      try {
        // 1) 캐시된 데모 세션 확인 — 없으면 새로 생성(모태 복제), 있으면 재사용
        let session = null;
        try {
          const cached = localStorage.getItem(DEMO_SESSION_KEY);
          if (cached) session = JSON.parse(cached);
        } catch {}

        if (!session || !session.ownerUid || !session.parentUid) {
          const cs = await fetch(APP_CONFIG.createDemoSessionUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: '{}',
          });
          if (!cs.ok) throw new Error('create session failed');
          session = await cs.json();
          try { localStorage.setItem(DEMO_SESSION_KEY, JSON.stringify(session)); } catch {}
        }

        const uid = session[cfg.key];

        // 2) 그 uid로 토큰 발급 후 로그인
        const res = await fetch(APP_CONFIG.devLoginTokenUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ uid }),
        });
        if (!res.ok) throw new Error('token failed');
        const { customToken } = await res.json();
        await signInWithCustomToken(auth, customToken);
        if (!alive) return;

        // 로고 최소 표시 + AuthContext userData 로드 시간
        await introDelay;
        await new Promise((r) => setTimeout(r, 500));
        if (!alive) return;
        setMode(cfg.mode);
        navigate(cfg.to, { replace: true });
      } catch (e) {
        console.error('데모 진입 실패:', e);
        if (alive) navigate('/app/home', { replace: true });
      }
    })();
    return () => { alive = false; };
  }, [cfg, navigate, setMode]);

  return (
    <Wrap>
      <Logo>1등급</Logo>
      <Title>{cfg.label} 가상 체험 공간으로 이동합니다</Title>
      <Sub>잠시만 기다려 주세요</Sub>
      <Spinner />
    </Wrap>
  );
};

export default DemoIntroPage;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 32px;
  background: #fff;
`;

const Logo = styled.p`
  font-family: 'Poppins', sans-serif;
  font-size: 56px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  letter-spacing: -1px;
`;

const Title = styled.p`
  margin-top: 14px;
  font-size: 17px;
  font-weight: 700;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.p`
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const Spinner = styled.div`
  margin-top: 6px;
  width: 30px;
  height: 30px;
  border: 3px solid #E5E7EB;
  border-top-color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;
